import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import {
  MapInput,
  MapMarker,
  MapMarkerOptions,
} from '@features/map/interfaces';
import { TripPoint } from '@pages/trips-page/interfaces';
import CustomGridFilterUtils from '@core/utils/custom-grid-filter.utils';
import { TripInformationTabForm } from '@pages/trips-page/components/trip-information-tab/interfaces';

export default class TripUtils {
  static mapTripPointsForMap(
    points: TripPoint[] | undefined,
    distance?: string,
    showTripMarkers = false
  ): MapMarker[][] {
    if (!points?.length) return [];

    const suitablePoints = points.filter(point =>
      showTripMarkers ? !!point.UTCTime : point
    );

    const linesByHide = TripUtils.splitByHide(suitablePoints);

    const result = linesByHide.map((lineArr, arrIndex) =>
      lineArr.map((point, itemIndex) => ({
        lat: point.Lat,
        lng: point.Lon,
        options:
          arrIndex === linesByHide.length - 1 &&
          itemIndex === lineArr.length - 1 &&
          distance
            ? {
                tooltipValue: distance + 'km',
                isToolTipPermanent: true,
              }
            : this.getMapPointOptions(point, showTripMarkers),
      }))
    );

    return result;
  }

  static getTripMapInput(
    tripForm: TripInformationTabForm,
    showRouteMarkers = false
  ): MapInput[] {
    const tripPointsForSegment: MapMarker[][] = TripUtils.mapTripPointsForMap(
      tripForm.TripPoints?.length
        ? tripForm.TripPoints
        : [
            { Lat: tripForm.StartGPSLat, Lon: tripForm.StartGPSLon },
            { Lat: tripForm.EndGPSLat, Lon: tripForm.EndGPSLon },
          ],
      String(tripForm.Distance),
      showRouteMarkers
    );

    const tripPointInputs: MapInput[] = [];
    tripPointsForSegment.forEach(pointsArr => {
      tripPointInputs.push({ isRouteInput: true, points: pointsArr });
    });

    return tripPointInputs;
  }

  static getDefaultTripGridFilter(): FilterModelWithKeys {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return CustomGridFilterUtils.getStartTimeCustomPeriodFilters(
      today,
      tomorrow
    );
  }

  private static getMapPointOptions(
    point: TripPoint,
    showTripMarkers: boolean
  ): MapMarkerOptions {
    const options = { opacity: point.Hide ? 1 : undefined } as MapMarkerOptions;

    if (!showTripMarkers) return options;

    return {
      ...options,
      isToolTipPermanent: false,
      tooltipValue: point.UTCTime
        ? new Date(point.UTCTime).toLocaleDateString('no', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : '',
      customMarker: {
        disableHoverAnimation: true,
        iconUrl: './assets/trip-marker.svg',
        iconSize: [14, 14],
      },
    };
  }

  private static splitByHide(arr: TripPoint[]): TripPoint[][] {
    const result: TripPoint[][] = [];
    let visibleGroup: TripPoint[] = [];
    let hiddenGroup: TripPoint[] = [];

    arr.forEach((item, index) => {
      if (item.Hide) {
        if (visibleGroup.length > 0) {
          result.push(visibleGroup);
          visibleGroup = [];
        }
        hiddenGroup.push(item);
      } else {
        if (hiddenGroup.length) {
          result.push(hiddenGroup);
          hiddenGroup = [];
        }
        visibleGroup.push(item);
      }

      if (index === arr.length - 1) {
        if (visibleGroup.length) {
          result.push(visibleGroup);
        }
        if (hiddenGroup.length) {
          result.push(hiddenGroup);
        }
      }
    });

    return result;
  }
}
